import React, { useState, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { useI18n } from '@/lib/i18n';
import { X, UploadCloud, FileAudio, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { validateTracklist, normalizeTracklist, formatDuration } from '@/lib/tracklist';

const MAX_MB = 500;
const ACCEPT = ['.mp3', '.m4a', '.wav'];

function readAudioDuration(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const d = audio.duration;
      URL.revokeObjectURL(url);
      resolve(d);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('metadata'));
    };
    audio.src = url;
  });
}

// Number of hours (one part per hour) from the show's start/end times.
function showHours(show) {
  const toMin = (t) => {
    const [h, m] = (t || '0:0').split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };
  let diff = toMin(show.end_time) - toMin(show.start_time);
  if (diff <= 0) diff += 24 * 60; // overnight show
  return Math.max(1, Math.round(diff / 60));
}

export default function UploadForm({ show, airDate, existingParts, userEmail, onClose, onDone, adminOnBehalf = false, actingHostId = null }) {
  const { t } = useI18n();
  const hours = showHours(show);
  const uploadedPartNums = new Set((existingParts || []).map((p) => Number(p.part) || 0));
  const allParts = Array.from({ length: hours }, (_, i) => i + 1);
  const selectableParts = allParts.filter((n) => !uploadedPartNums.has(n));
  const defaultPart = selectableParts[0] || 1;

  const [part, setPart] = useState(defaultPart);
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [sizeMb, setSizeMb] = useState(0);
  const [reading, setReading] = useState(false);
  const [tracklist, setTracklist] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const fileRef = useRef(null);
  const txtFileRef = useRef(null);

  const tracklistWarnings = validateTracklist(tracklist, duration);
  const canSubmit =
    !!file && !reading && !uploading && duration > 0 && selectableParts.includes(Number(part));

  const tr = (w) => {
    let msg = t(w.key);
    if (w.vars) for (const [k, v] of Object.entries(w.vars)) msg = msg.replace(`{${k}}`, v);
    return msg;
  };

  const handleFile = async (f) => {
    setError('');
    if (!f) return;
    const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
    if (!ACCEPT.includes(ext)) {
      setError(t('Only .mp3, .m4a, or .wav files are allowed.'));
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setError(t('File exceeds 500 MB.'));
      return;
    }
    setFile(f);
    setSizeMb(Math.round((f.size / (1024 * 1024)) * 10) / 10);
    setDuration(0);
    setReading(true);
    try {
      const d = await readAudioDuration(f);
      setDuration(Math.round(d));
    } catch {
      setError(t('Could not read audio duration.'));
    }
    setReading(false);
  };

  const handleTxtFile = async (f) => {
    if (!f) return;
    const text = await f.text();
    setTracklist(normalizeTracklist(text));
  };

  const handleNormalize = () => {
    if (tracklist) setTracklist(normalizeTracklist(tracklist));
  };

  const submit = async () => {
    if (!canSubmit) return;
    setUploading(true);
    setProgress(0);
    // Asymptotic creep toward 92% so a long upload keeps moving instead of
    // slamming to a fake 90% in five seconds and looking stuck.
    const timer = setInterval(
      () => setProgress((p) => Math.min(92, p + Math.max(0.4, (92 - p) * 0.06))),
      400
    );
    try {
      const { file_uri } = await base44.integrations.Core.UploadPrivateFile({ file });
      if (adminOnBehalf) {
        // Admin uploading on behalf of a DJ: route through the admin-only
        // backend function so host_id = the chosen host and uploaded_by is
        // recorded as the admin's email (server-side). The send_email flag
        // controls whether the DJ gets the upload-received confirmation.
        const res = await base44.functions.invoke('adminCreateMix', {
          show_id: show.id,
          host_id: actingHostId || show.host_id,
          air_date: airDate,
          part: Number(part),
          file_uri,
          file_name: file.name,
          file_size_mb: sizeMb,
          duration_seconds: duration,
          tracklist,
          send_email: sendEmail,
        });
        if (res.data?.error) throw new Error(res.data.error);
      } else {
        const payload = {
          show_id: show.id,
          host_id: show.host_id,
          air_date: airDate,
          part: Number(part),
          file_uri,
          file_name: file.name,
          file_size_mb: sizeMb,
          duration_seconds: duration,
          tracklist,
          status: 'uploaded',
          uploaded_by_email: userEmail,
        };
        const existing = existingParts.find(
          (p) => p.air_date === airDate && Number(p.part) === Number(part)
        );
        if (existing) {
          await base44.entities.MixUpload.update(existing.id, payload);
        } else {
          await base44.entities.MixUpload.create(payload);
        }
      }
      clearInterval(timer);
      setProgress(100);
      onDone();
    } catch (e) {
      clearInterval(timer);
      setError(e.message || t('Upload failed.'));
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-black text-base font-bold">
            {t('Upload part')} — {show.name}
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-black" disabled={uploading}>
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-neutral-700 text-sm font-medium mb-1.5 block">
              {t('Part')}
            </label>
            <div className="flex flex-wrap gap-2">
              {allParts.map((n) => {
                const taken = uploadedPartNums.has(n);
                const selected = Number(part) === n;
                return (
                  <button
                    key={n}
                    type="button"
                    disabled={taken || uploading}
                    onClick={() => setPart(n)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg border text-sm font-semibold transition-colors',
                      selected
                        ? 'bg-purple-700 text-white border-purple-700'
                        : 'bg-white text-neutral-700 border-neutral-200 hover:border-purple-300',
                      taken && 'opacity-40 cursor-not-allowed line-through'
                    )}
                  >
                    {t('Part')} {n} {t('of')} {hours}
                  </button>
                );
              })}
            </div>
            {selectableParts.length === 0 && (
              <p className="text-neutral-500 text-xs mt-1.5">{t('All parts uploaded.')}</p>
            )}
          </div>

          <div>
            <label className="text-neutral-700 text-sm font-medium mb-1.5 block">
              {t('Audio File')} (.mp3, .m4a, .wav — max 500 MB)
            </label>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="block w-full text-left border border-dashed border-neutral-300 rounded-lg px-4 py-3 hover:border-purple-400 hover:bg-purple-50/30 transition-colors disabled:opacity-50"
            >
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700">
                <UploadCloud size={16} /> {file ? file.name : t('Choose file')}
              </span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".mp3,.m4a,.wav,audio/mpeg,audio/mp4,audio/x-m4a,audio/wav"
              onChange={(e) => handleFile(e.target.files?.[0])}
              disabled={uploading}
              className="hidden"
            />
            {reading && (
              <p className="text-neutral-500 text-xs mt-2 flex items-center gap-1">
                <Loader2 size={12} className="animate-spin" /> {t('Reading audio metadata…')}
              </p>
            )}
            {file && !reading && (
              <div className="mt-2 flex items-center gap-2 text-xs text-neutral-600">
                <FileAudio size={14} />
                <span className="truncate max-w-[60%] font-medium">{file.name}</span>
                <span>· {sizeMb} MB · {formatDuration(duration)}</span>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-neutral-700 text-sm font-medium">
                {t('Tracklist')} <span className="text-neutral-400 font-normal">({t('Optional')})</span>
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleNormalize}
                  disabled={!tracklist || uploading}
                  className="text-xs text-purple-700 font-semibold hover:underline disabled:opacity-40"
                >
                  {t('Normalize')}
                </button>
                <button
                  type="button"
                  onClick={() => txtFileRef.current?.click()}
                  disabled={uploading}
                  className="text-xs text-purple-700 font-semibold hover:underline disabled:opacity-40"
                >
                  {t('Load from .txt/.csv')}
                </button>
                <input
                  ref={txtFileRef}
                  type="file"
                  accept=".txt,.csv,text/plain,text/csv"
                  className="hidden"
                  onChange={(e) => handleTxtFile(e.target.files?.[0])}
                />
              </div>
            </div>
            <textarea
              value={tracklist}
              onChange={(e) => setTracklist(e.target.value)}
              rows={6}
              disabled={uploading}
              placeholder={'0:00 Bad Bunny - DtMF\n3:12 Feid - Luna\n1:02:45 Wisin & Yandel - Rakata'}
              className="w-full px-3 py-2 rounded-lg border border-neutral-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none font-mono text-xs"
            />
            <p className="text-neutral-400 text-xs mt-1">
              {t('Paste a Serato/Rekordbox export or type "time Artist - Title" per line. We parse it loosely.')}
            </p>
            {tracklistWarnings.length > 0 && tracklist && (
              <ul className="mt-1.5 space-y-0.5">
                {tracklistWarnings.map((w, i) => (
                  <li key={i} className="text-amber-600 text-xs">{tr(w)}</li>
                ))}
              </ul>
            )}
          </div>

          {adminOnBehalf && (
            <label className="flex items-start gap-2.5 p-3 rounded-lg bg-purple-50 border border-purple-200 cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={uploading}
                className="mt-0.5 w-4 h-4 accent-purple-700"
              />
              <span className="text-sm text-neutral-800">
                <span className="font-semibold">Email the DJ that their mix was received</span>
                <span className="block text-neutral-500 text-xs mt-0.5">
                  Turn off for mixes the DJ sent another way.
                </span>
              </span>
            </label>
          )}

          {error && <p className="text-red-600 text-sm font-medium">{error}</p>}

          {uploading && (
            <div>
              <div className="flex justify-between text-xs text-neutral-500 mb-1">
                <span>{t('Uploading…')} {sizeMb} MB</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-700 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              disabled={uploading}
              className="px-4 py-2 rounded-lg border border-neutral-200 text-neutral-700 font-semibold hover:bg-neutral-50 disabled:opacity-50"
            >
              {t('Cancel')}
            </button>
            <button
              onClick={submit}
              disabled={!canSubmit}
              className="px-5 py-2 rounded-lg bg-purple-700 text-white font-bold hover:bg-purple-800 disabled:opacity-50 flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> {t('Uploading…')}
                </>
              ) : (
                <>
                  <UploadCloud size={16} /> {t('Upload part')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}